import { PaginationControls } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDimensionManagement } from "@/hooks/use-dimension";
import { createPaginationProps } from "@/types/pagination.types";
import { DEFAULT_DEBOUNCE_MS } from "@/utils/debounce";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { SearchBar } from "../../components/ui/search-bar";
import { DimensionTable } from "./components/dimension-table";
import { useEnrichedDimensionsWithDefinitions } from "./hooks/use-enriched-dimensions-with-definitions";

export const Route = createFileRoute(APP_ROUTES_FULL.AI_DIMENSIONS)({
    component: AIDimensions,
    staticData: {
        config: {
            breadcrumb: { title: "dimensions", to: APP_ROUTES_TO.AI_DIMENSIONS },
            title: "dimensions",
        },
    },
});

function AIDimensions() {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState("");

    const { data, isLoading, error, handlers } = useDimensionManagement();

    // Enrich dimensions with definition names
    const { dimensions: enrichedDimensions, isLoadingDefinitions } = useEnrichedDimensionsWithDefinitions(
        data?.data || []
    );

    // Create pagination props using the utility (accepts undefined safely)
    const paginationProps = createPaginationProps({
        response: data,
        onPageChange: handlers.setPage,
        isLoading,
    });

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center space-y-4 h-full w-full">
                <p className="text-lg font-medium text-destructive">{t("error_loading_data")}</p>
                <p className="text-sm text-muted-foreground">{error?.message || t("something_went_wrong")}</p>
            </div>
        );
    }

    return (
        <>
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">{t("dimensions")}</h1>
                    <p className="text-muted-foreground">{t("view_dimensions_description")}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <SearchBar
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={t("search_dimensions")}
                        debounceMs={DEFAULT_DEBOUNCE_MS}
                        minLength={0}
                        onDebouncedChange={handlers.setSearch}
                        className="sm:w-60"
                    />
                </div>
            </div>

            {/* Search Status */}
            {searchValue.trim() && !isLoading && (
                <div className="px-6 py-2 bg-muted/50 border-y text-sm text-muted-foreground">
                    {data?.data?.length
                        ? t("search_results_found", {
                              count: data.pagination?.total ?? data.data.length,
                              term: searchValue.trim(),
                          })
                        : t("no_search_results", { term: searchValue.trim() })}
                </div>
            )}

            {/* Table */}
            <div className="flex-1 min-h-0 overflow-hidden">
                <div className="flex flex-col h-full min-h-0 pb-8">
                    <DimensionTable data={enrichedDimensions} isLoading={isLoading || isLoadingDefinitions} />
                </div>
            </div>

            {/* Pagination */}
            {paginationProps && (
                <div className="shrink-0 border-t bg-background/95 backdrop-blur-sm px-6 py-6 mt-auto sticky bottom-0">
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-sm text-muted-foreground min-w-0 shrink-0">
                            {t("showing_results", {
                                start: (paginationProps.page - 1) * paginationProps.pageSize + 1,
                                end: Math.min(paginationProps.page * paginationProps.pageSize, paginationProps.total),
                                total: paginationProps.total,
                            })}
                        </p>
                        <PaginationControls
                            currentPage={paginationProps.page}
                            totalPages={paginationProps.totalPages}
                            onPageChange={paginationProps.onPageChange!}
                            isLoading={paginationProps.isLoading}
                            className="shrink-0"
                        />
                    </div>
                </div>
            )}
        </>
    );
}
